import { QuestionMarkIcon } from "@rothoblaas/design-system";
import classNames from "classnames";
import { Fragment } from "react";
import Icon from "../../../components/ui/Icon";
import SvgNoImage from "../../../components/ui/SvgNoImage";
import useI18n from "../../../hooks/useTranslations";
import styles from "./ConnectorsCard.module.css";
import OptionsStatus from "./OptionsStatus";

type FamilyOption = {
  code: string;
  name: string;
};

type FamilyOptionsGridProps<T extends FamilyOption> = {
  options: T[];
  value: T | null;
  imageByCode: Record<string, string>;
  isLoading: boolean;
  hasError: boolean;
  onSelect: (option: T) => void;
  // With onHelp, each option is wrapped to host a help button - shown for the options hasHelp accepts.
  hasHelp?: (option: T) => boolean;
  onHelp?: (option: T) => void;
};

// The connector and support family pickers: one image button per family. The only option can't be
// picked again once it's selected.
const FamilyOptionsGrid = <T extends FamilyOption>({
  options,
  value,
  imageByCode,
  isLoading,
  hasError,
  onSelect,
  hasHelp,
  onHelp,
}: FamilyOptionsGridProps<T>) => {
  const i18n = useI18n();

  return (
    <div className={styles.grid}>
      <OptionsStatus className={styles.option} isLoading={isLoading} hasError={hasError} />
      {options.map((option) => {
        const label = option.name;
        const isSelected = value?.code === option.code;
        const image = imageByCode[option.code];
        const optionButton = (
          <button
            type="button"
            className={classNames(styles.option, { [styles.selected]: isSelected })}
            aria-pressed={isSelected}
            disabled={options.length === 1 && !!value}
            onClick={() => onSelect(option)}
          >
            {image ? (
              <img className={styles.image} src={image} alt={label} />
            ) : (
              <SvgNoImage className={styles.image} aria-label={label} />
            )}
            <span className={styles.label}>{label}</span>
          </button>
        );

        if (!onHelp) {
          return <Fragment key={option.code}>{optionButton}</Fragment>;
        }

        return (
          <div key={option.code} className={styles.optionWrapper}>
            {optionButton}
            {hasHelp?.(option) && (
              <button
                type="button"
                className={styles.help}
                aria-label={i18n.translation.common.help}
                onClick={(event) => {
                  event.stopPropagation();
                  onHelp(option);
                }}
              >
                <Icon glyph={QuestionMarkIcon} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FamilyOptionsGrid;
